/**
 * TypeScript interfaces and types for ESAL (Equivalent Single Axle Load) calculations
 * Based on Excel data structure from temp_esal_material/TRAFFIC LOAD.txt
 */

/** Configuration for loading scenarios and weight distribution */
export interface LoadingScenarioConfig {
    /** Empty load multiplier (default: 0.1 = 10%) */
    emptyMultiplier: number;
    /** Half full load multiplier (default: 0.5 = 50%) */
    halfFullMultiplier: number;
    /** Overload multiplier (default: 1.15 = 115%) */
    overloadMultiplier: number;
    /** Empty scenario percentage (default: 20%) */
    emptyPercentage: number;
    /** Half full scenario percentage (default: 20%) */
    halfFullPercentage: number;
    /** Fully loaded scenario percentage (default: 50%) */
    fullyLoadedPercentage: number;
    /** Overloaded scenario percentage (default: 10%) */
    overloadedPercentage: number;
}

/** Default loading scenario configuration based on Excel patterns */
export const DEFAULT_LOADING_CONFIG: LoadingScenarioConfig = {
    emptyMultiplier: 0.1, // 10% of design load
    halfFullMultiplier: 0.5, // 50% of design load
    overloadMultiplier: 1.15, // 115% of design load
    emptyPercentage: 20, // 20% of time
    halfFullPercentage: 20, // 20% of time
    fullyLoadedPercentage: 50, // 50% of time
    overloadedPercentage: 10, // 10% of time
};

/** Base truck configuration data from Excel */
export interface TruckConfiguration {
    /** Truck identifier (1-8) */
    id: number;
    /** Display name (Camion1, Camion2, etc.) */
    name: string;
    /** Number of axles (fixed from Excel) */
    axles: number;
    /** Design total load in kN (fixed from Excel) */
    designTotalLoad: number;
    /** Excel cell reference for Direction 1 vehicles */
    direction1Cell: string;
    /** Excel cell reference for Direction 2 vehicles */
    direction2Cell: string;
    /** Path to truck image (e.g. Camion1.png) */
    imageSrc: string;
}

/** User-editable truck data */
export interface TruckData {
    /** Truck identifier (1-8) */
    id: number;
    /** Whether truck is selected for calculations */
    selected: boolean;
    /** Display name (Camion1, Camion2, etc.) */
    name: string;
    /** Number of axles (fixed from Excel) */
    axles: number;
    /** Design total load in kN (fixed from Excel) */
    designTotalLoad: number;
    /** User-defined custom total load in kN */
    customTotalLoad: number;
    /** Number of vehicles per day in Direction 1 */
    direction1Vehicles: number;
    /** Number of vehicles per day in Direction 2 */
    direction2Vehicles: number;
    /** Calculated ESAL per day for this truck */
    esalPerDay: number;
    /** Percentage of total passages */
    passagePercentage: number;
    /** Path to truck image (require result) */
    imageSrc: any;
}

/** EALF (Equivalent Axle Load Factor) calculation parameters */
export interface EALFParameters {
    /** Steering axle EALF */
    steeringAxle: number;
    /** Single axle EALF */
    singleAxle: number;
    /** Tandem axle EALF */
    tandemAxle: number;
    /** Tridem axle EALF */
    tridemAxle: number;
    /** Total equivalent EALF per vehicle */
    totalPerVehicle: number;
}

/** Summary calculation data displayed in right panel */
export interface SummaryCalculationData {
    /** Total number of passages N1 */
    totalPassages: number;
    /** Directional distribution factor (typically 0.5) */
    directionalDistribution: number;
    /** Lane distribution factor (typically 0.90) */
    laneDistribution: number;
    /** Analysis period in years (typically 12) */
    analysisPeriod: number;
    /** Year growth rate as percentage (typically 2.00%) */
    yearGrowthRate: number;
    /** Compound Traffic Growth Factor */
    compoundTrafficGrowthFactor: number;
    /** Total volume of traffic during analysis period (w'18) */
    totalVolumeTraffic: number;
    /** Traffic in design lane (w18 = DD * DL * w'18) */
    trafficDesignLane: number;
    /** User defined traffic in design lane (0 = use calculated) */
    userDefinedTrafficDesignLane: number;
}

/** Complete ESAL calculation state */
export interface ESALCalculationState {
    /** Array of truck data for all 8 trucks */
    trucks: TruckData[];
    /** Summary calculation results */
    summaryData: SummaryCalculationData;
    /** Total ESAL from all selected trucks */
    totalESAL: number;
    /** Loading state for calculations */
    isCalculating: boolean;
    /** Any calculation errors */
    calculationError: string | null;
}

/** Result from ESAL automatic calculation */
export interface EsalCalculationResult {
    /** Final ESAL value for design lane (H91 = w18 = DD * DL * w'18) */
    esalFinalValue: number;
    /** Total passages per year (H84 = Q82*365) - needed for canvas tables */
    totalPassagesPerYear: number;
}

/** Props for the main ESAL calculation modal */
export interface EsalCalculationModalProps {
    /** Whether the modal is open */
    open: boolean;
    /** Callback when modal should be closed */
    onClose: () => void;
    /** Callback when calculation is complete with result (number for backward compatibility, object for new automatic mode) */
    onCalculationComplete: (result: number | EsalCalculationResult) => void;
}

/** Props for truck data grid component */
export interface TruckDataGridProps {
    /** Array of truck data */
    trucks: TruckData[];
    /** Callback when truck data is updated */
    onTruckUpdate: (updatedTruck: TruckData) => void;
    /** Whether the grid is in edit mode */
    isEditable: boolean;
    /** Optional project data for dynamic SN calculation */
    projectData?: any;
    /** Validity of each numeric cell, keyed `row:field`: the owner blocks its action while any key is invalid */
    onValidityChange?: (key: string, isValid: boolean) => void;
    /** A change puts every cell back on the stored value (owner reset, close, reopen) */
    resetKey?: string | number;
}

/** Props for calculations summary component */
export interface CalculationsSummaryProps {
    /** Summary calculation data to display */
    summaryData: SummaryCalculationData;
    /** Total ESAL from all trucks */
    totalESAL: number;
    /** Loading state */
    isCalculating: boolean;
    /** Callback when summary calculation inputs change */
    onSummaryInputsChange?: (inputs: { dd: number; dl: number; analysisPeriod: number; growthRate: number; trafficDesignLane: number }) => void;
    /** Validity of each numeric cell, keyed `row:field`: the owner blocks its action while any key is invalid */
    onValidityChange?: (key: string, isValid: boolean) => void;
    /** A change puts every cell back on the stored value (owner reset, close, reopen) */
    resetKey?: string | number;
}

/** Excel formula calculation input parameters */
export interface FormulaCalculationInput {
    /** Vehicle weight in kN */
    totalWeight: number;
    /** Number of vehicles per day */
    vehiclesPerDay: number;
    /** Percentage of total passages */
    passagePercentage: number;
    /** Number of axles */
    axles: number;
    /** Steering axle load in kN */
    steeringAxleLoad: number;
    /** Single axle load in kN */
    singleAxleLoad: number;
    /** Tandem axle load in kN */
    tandemAxleLoad: number;
    /** Tridem axle load in kN */
    tridemAxleLoad: number;
}

/**
 * ESAL Constants - Loaded from centralized esal-constants.json
 *
 * MIGRATION: Eliminates 150+ lines of duplicate constants across ESALTypes.ts and ESALTypesAircraft.ts
 * Single source of truth: src/app/data/esal-constants.json
 * Service loader: ESALConstantsService.getConstants('paved')
 *
 * This transformation maintains backward compatibility with existing UPPER_SNAKE_CASE property names
 * while loading from the centralized camelCase service.
 */
import { ESALConstantsService } from '../../../../services/ESALConstantsService';

// Load constants for Paved project type
const _pavedConstants = ESALConstantsService.getConstants('paved');

/** Constants from Excel calculations (loaded from esal-constants.json) */
export const ESAL_CONSTANTS = {
    /** Structural Number (SN) */
    STRUCTURAL_NUMBER: _pavedConstants.structuralNumber,
    /** Present Serviceability (Pt) */
    PRESENT_SERVICEABILITY: _pavedConstants.presentServiceability,
    /** Load conversion factor (kN to kips) */
    KN_TO_KIPS: _pavedConstants.knToKips,
    /** Default directional distribution factor */
    DEFAULT_DD: _pavedConstants.defaultDD,
    /** Default lane distribution factor */
    DEFAULT_DL: _pavedConstants.defaultDL,
    /** Default analysis period (years) */
    DEFAULT_ANALYSIS_PERIOD: _pavedConstants.defaultAnalysisPeriod,
    /** Default year growth rate (%) */
    DEFAULT_GROWTH_RATE: _pavedConstants.defaultGrowthRate,
    /** Beta factor base value - AASHTO coefficient */
    BETA_BASE: _pavedConstants.betaBase,
    /** Beta factor coefficient - AASHTO coefficient */
    BETA_COEFFICIENT: _pavedConstants.betaCoefficient,
    /** Log base for calculations - AASHTO coefficient */
    LOG_BASE: _pavedConstants.logBase,
    /** AASHTO formula exponents and coefficients */
    AASHTO_EXPONENTS: {
        /** Load exponent in beta calculation (3.23) */
        LOAD_EXPONENT: _pavedConstants.aashtoExponents.loadExponent,
        /** Structural number exponent in beta calculation (5.19) */
        SN_EXPONENT: _pavedConstants.aashtoExponents.snExponent,
        /** Load exponent in EALF calculation (4.79) */
        EALF_LOAD_EXPONENT: _pavedConstants.aashtoExponents.ealfLoadExponent,
        /** Axle constant exponent in EALF calculation (4.33) */
        EALF_AXLE_EXPONENT: _pavedConstants.aashtoExponents.ealfAxleExponent,
    },
    /** AASHTO axle type constants */
    AXLE_CONSTANTS: {
        /** Steering axle constant */
        STEERING: _pavedConstants.axleConstants.steering,
        /** Single axle constant */
        SINGLE: _pavedConstants.axleConstants.single,
        /** Tandem axle constant */
        TANDEM: _pavedConstants.axleConstants.tandem,
        /** Tridem axle constant */
        TRIDEM: _pavedConstants.axleConstants.tridem,
    },
} as const;

/** Default truck configurations based on Excel data */
export const DEFAULT_TRUCK_CONFIGURATIONS: TruckConfiguration[] = [
    {
        id: 1,
        name: 'Camion1',
        axles: 2,
        designTotalLoad: 180,
        direction1Cell: 'C10',
        direction2Cell: 'C47',
        imageSrc: require('../../../../images/camion/Camion1-t.png'),
    },
    {
        id: 2,
        name: 'Camion2',
        axles: 3,
        designTotalLoad: 250,
        direction1Cell: 'C14',
        direction2Cell: 'C51',
        imageSrc: require('../../../../images/camion/Camion2-t.png'),
    },
    {
        id: 3,
        name: 'Camion3',
        axles: 4,
        designTotalLoad: 360,
        direction1Cell: 'C18',
        direction2Cell: 'C55',
        imageSrc: require('../../../../images/camion/Camion3-t.png'),
    },
    {
        id: 4,
        name: 'Camion4',
        axles: 5,
        designTotalLoad: 400,
        direction1Cell: 'C22',
        direction2Cell: 'C59',
        imageSrc: require('../../../../images/camion/Camion4-t.png'),
    },
    {
        id: 5,
        name: 'Camion5',
        axles: 5,
        designTotalLoad: 400,
        direction1Cell: 'C26',
        direction2Cell: 'C63',
        imageSrc: require('../../../../images/camion/Camion5-t.png'),
    },
    {
        id: 6,
        name: 'Camion6',
        axles: 6,
        designTotalLoad: 400,
        direction1Cell: 'C30',
        direction2Cell: 'C67',
        imageSrc: require('../../../../images/camion/Camion6-t.png'),
    },
    {
        id: 7,
        name: 'Camion7',
        axles: 7,
        designTotalLoad: 600,
        direction1Cell: 'C34',
        direction2Cell: 'C71',
        imageSrc: require('../../../../images/camion/Camion7-t.png'),
    },
    {
        id: 8,
        name: 'Camion8',
        axles: 8,
        designTotalLoad: 600,
        direction1Cell: 'C38',
        direction2Cell: 'C75',
        imageSrc: require('../../../../images/camion/Camion8-t.png'),
    },
]; // Remove as const for compatibility

/** Default truck data values from Excel analysis - ALL trucks pre-selected */
export const DEFAULT_TRUCK_VALUES: Partial<TruckData>[] = [
    {
        // Camion1 - from Excel C10=100, USER LOAD=200, DESIGN=180, Q10=592.99
        id: 1,
        selected: true,
        direction1Vehicles: 100,
        direction2Vehicles: 0, // Excel shows 0 for Direction 2
        customTotalLoad: 200, // Excel USER'S TOTAL LOAD (C13)
        passagePercentage: 100,
    },
    {
        // Camion2 - from Excel C14=110, USER LOAD=270, DESIGN=250, Q14=336.86
        id: 2,
        selected: true,
        direction1Vehicles: 110,
        direction2Vehicles: 0, // Excel shows 0 for Direction 2
        customTotalLoad: 270, // Excel USER'S TOTAL LOAD (C17)
        passagePercentage: 100,
    },
    {
        // Camion3 - from Excel C18=150, C55=440, USER LOAD=400, DESIGN=360, Q18=942.90, Q55=2765.83
        id: 3,
        selected: true,
        direction1Vehicles: 150,
        direction2Vehicles: 440, // Excel Direction 2 value
        customTotalLoad: 400, // Excel USER'S TOTAL LOAD (C21)
        passagePercentage: 100,
    },
    {
        // Camion4 - from Excel C22=150, USER LOAD=440, DESIGN=400, Q22=586.38
        id: 4,
        selected: true,
        direction1Vehicles: 150,
        direction2Vehicles: 0, // Excel shows 0 for Direction 2
        customTotalLoad: 440, // Excel USER'S TOTAL LOAD (C25)
        passagePercentage: 100,
    },
    {
        // Camion5 - from Excel C26=150, USER LOAD=440, DESIGN=400, Q26=603.66
        id: 5,
        selected: true,
        direction1Vehicles: 150,
        direction2Vehicles: 0, // Excel shows 0 for Direction 2
        customTotalLoad: 440, // Excel USER'S TOTAL LOAD (C29)
        passagePercentage: 100,
    },
    {
        // Camion6 - from Excel C30=150, C67=600, USER LOAD=440, DESIGN=400, Q30=328.88, Q67=1315.53
        id: 6,
        selected: true,
        direction1Vehicles: 150,
        direction2Vehicles: 600, // Excel Direction 2 value
        customTotalLoad: 440, // Excel USER'S TOTAL LOAD (C33)
        passagePercentage: 100,
    },
    {
        // Camion7 - from Excel C34=0, USER LOAD=650, DESIGN=600, Q34=0
        id: 7,
        selected: true,
        direction1Vehicles: 0,
        direction2Vehicles: 0,
        customTotalLoad: 650, // Excel USER'S TOTAL LOAD (C37)
        passagePercentage: 100,
    },
    {
        // Camion8 - from Excel C38=0, USER LOAD=700, DESIGN=600, Q38=0
        id: 8,
        selected: true,
        direction1Vehicles: 0,
        direction2Vehicles: 0,
        customTotalLoad: 700, // Excel USER'S TOTAL LOAD (C41)
        passagePercentage: 100,
    },
];

/**
 * Inputs for CalculationsSummary onSummaryInputsChange callback
 */
export type CalculationsSummaryInputs = {
    dd: number;
    dl: number;
    analysisPeriod: number;
    growthRate: number;
    trafficDesignLane: number;
};
